import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { KeyRound, Mail, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { FullPageLoader } from './ui/page-loader';

export const QuestionBankAccessGate = () => {
  const navigate = useNavigate();
  const { api, refreshUser } = useAuth();
  const [code, setCode] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [codeRequested, setCodeRequested] = useState(false);

  const handleRequestCode = async () => {
    setRequesting(true);
    try {
      await api.post('/practice/access/request-code');
      setCodeRequested(true);
      toast.success('Access code sent to the administrator.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not request an access code.');
    } finally {
      setRequesting(false);
    }
  };

  const handleVerify = async (event) => {
    event.preventDefault();
    const normalized = code.replace(/\D/g, '').slice(0, 6);
    if (normalized.length !== 6) {
      toast.error('Enter the 6-digit access code.');
      return;
    }

    setVerifying(true);
    try {
      await api.post('/practice/access/verify', { code: normalized });
      await refreshUser();
      setAccessState({ gate_enabled: true, has_access: true });
      toast.success('Question bank unlocked.');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Invalid or expired access code.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border bg-card p-8 shadow-sm">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ShieldCheck className="h-6 w-6" />
        </div>
        <h1 className="text-center text-2xl font-black font-cabinet">Question bank access</h1>
        <p className="mt-2 text-center text-sm text-muted-foreground">
          Med900 has launched. To use the question bank, request an authentication code.
          It will be emailed to the administrator, who can share it with you.
        </p>

        <div className="mt-6 space-y-3">
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={handleRequestCode}
            disabled={requesting}
            data-testid="qb-request-code-btn"
          >
            {requesting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Mail className="mr-2 h-4 w-4" />
            )}
            {codeRequested ? 'Resend code to admin' : 'Request access code'}
          </Button>

          {codeRequested && (
            <p className="text-center text-xs text-muted-foreground">
              Check with the Med900 team if you do not receive a code shortly.
            </p>
          )}
        </div>

        <form onSubmit={handleVerify} className="mt-6 space-y-4">
          <div>
            <label htmlFor="qb-access-code" className="mb-1.5 block text-sm font-medium">
              Enter access code
            </label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="qb-access-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="pl-9 text-center text-lg tracking-[0.35em] font-mono"
                data-testid="qb-access-code-input"
              />
            </div>
          </div>
          <Button type="submit" className="w-full" disabled={verifying || code.length !== 6} data-testid="qb-verify-code-btn">
            {verifying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Unlock question bank
          </Button>
        </form>

        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="mt-4 w-full text-center text-sm text-muted-foreground hover:text-foreground"
        >
          Back to dashboard
        </button>
      </div>
    </div>
  );
};

const ACCESS_TTL_MS = 5 * 60 * 1000;
const accessCache = new Map();

export const QuestionBankAccessRequired = ({ children }) => {
  const { api, user, loading, isAdmin } = useAuth();
  const cached = user ? accessCache.get(user.id) : null;
  const cacheValid = cached && Date.now() - cached.at < ACCESS_TTL_MS;
  const [accessState, setAccessState] = useState(cacheValid ? cached.data : null);
  const [checking, setChecking] = useState(!isAdmin && !cacheValid);

  useEffect(() => {
    let cancelled = false;

    const checkAccess = async () => {
      if (!user) {
        setChecking(false);
        return;
      }

      if (isAdmin) {
        setAccessState({ gate_enabled: false, has_access: true });
        setChecking(false);
        return;
      }

      const hit = accessCache.get(user.id);
      if (hit && Date.now() - hit.at < ACCESS_TTL_MS) {
        setAccessState(hit.data);
        setChecking(false);
        return;
      }

      try {
        const res = await api.get('/practice/access');
        if (!cancelled) {
          accessCache.set(user.id, { data: res.data, at: Date.now() });
          setAccessState(res.data);
        }
      } catch {
        if (!cancelled) {
          setAccessState({ gate_enabled: false, has_access: true });
        }
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    };

    if (!loading) {
      checkAccess();
    }

    return () => {
      cancelled = true;
    };
  }, [api, user, loading, isAdmin]);

  if (loading || checking) {
    return <FullPageLoader message="Checking access" />;
  }

  if (!accessState?.gate_enabled || accessState?.has_access) {
    return children;
  }

  return <QuestionBankAccessGate />;
};
